import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, UsersRound, Search, Loader2, RefreshCw, Link2, ExternalLink,
  Shield, Copy, Globe, Lock, UserPlus, Eye, User, Building
} from 'lucide-react';

export default function GroupLinksPage() {
  // Estados de usuários/conexões de clientes
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [userConnections, setUserConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [myConnections, setMyConnections] = useState([]); // Conexões do admin para entrar nos grupos
  const [selectedMyConnection, setSelectedMyConnection] = useState('');
  
  // Estados de grupos
  const [groups, setGroups] = useState([]);
  const [groupsWithLinks, setGroupsWithLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [joiningGroup, setJoiningGroup] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchMyConnections();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchUserConnections();
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedConnection) {
      fetchGroups();
    }
  }, [selectedConnection]);

  // Buscar todos os usuários (clientes/revendedores)
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Filtrar apenas revendedores e usuários normais (não admin)
        const clientUsers = data.filter(u => u.role !== 'admin');
        setUsers(clientUsers);
      }
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
    }
  };

  // Buscar minhas conexões (do admin) para usar para entrar nos grupos
  const fetchMyConnections = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const connected = data.filter(c => c.status === 'connected');
        setMyConnections(connected);
        if (connected.length > 0) {
          setSelectedMyConnection(connected[0].id);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar minhas conexões:', error);
    }
  };

  // Buscar conexões de um usuário específico
  const fetchUserConnections = async () => {
    if (!selectedUser) return;
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-links/user-connections/${selectedUser}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserConnections(data.connections || []);
        setSelectedConnection('');
        setGroups([]);
        setGroupsWithLinks([]);
      }
    } catch (error) {
      console.error('Erro ao buscar conexões do usuário:', error);
      toast.error('Erro ao buscar conexões');
    }
  };

  // Buscar grupos de uma conexão de cliente
  const fetchGroups = async () => {
    if (!selectedConnection) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-links/client-groups/${selectedConnection}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGroups(data.groups || []);
        setGroupsWithLinks([]);
      }
    } catch (error) {
      console.error('Erro ao buscar grupos:', error);
      toast.error('Erro ao buscar grupos');
    } finally {
      setLoading(false);
    }
  };

  const analyzeAllGroups = async () => {
    if (groups.length === 0) {
      toast.error('Nenhum grupo para analisar');
      return;
    }

    setAnalyzing(true);
    setAnalyzeProgress(0);
    const results = [];

    try {
      const token = localStorage.getItem('nexus-token');

      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        setAnalyzeProgress(((i + 1) / groups.length) * 100);

        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-links/analyze/${selectedConnection}/${encodeURIComponent(group.jid)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (res.ok) {
            const data = await res.json();
            results.push({
              ...group,
              ...data,
              analyzed: true
            });
          } else {
            results.push({
              ...group,
              analyzed: true,
              invite_link: null,
              found_links: [],
              description: null
            });
          }
        } catch (err) {
          results.push({
            ...group,
            analyzed: true,
            invite_link: null,
            found_links: [],
            error: err.message
          });
        }

        // Delay para não sobrecarregar
        if (i < groups.length - 1) {
          await new Promise(r => setTimeout(r, 500));
        }
      }

      setGroupsWithLinks(results);
      
      const withLinks = results.filter(g => g.invite_link || g.found_links?.length > 0);
      toast.success(`Análise concluída! ${withLinks.length} grupos com links encontrados.`);
    } catch (error) {
      console.error('Erro ao analisar grupos:', error);
      toast.error('Erro ao analisar grupos');
    } finally {
      setAnalyzing(false);
    }
  };

  // Entrar no grupo usando MINHA conexão (do admin)
  const joinGroup = async (inviteLink) => {
    if (!inviteLink) return;
    
    if (!selectedMyConnection) {
      toast.error('Selecione uma conexão sua para entrar no grupo');
      return;
    }
    
    setJoiningGroup(inviteLink);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-links/join`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          connection_id: selectedMyConnection, // Usar MINHA conexão
          invite_link: inviteLink
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Entrou no grupo: ${data.group_name || 'Sucesso!'}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao entrar no grupo');
      }
    } catch (error) {
      console.error('Erro ao entrar no grupo:', error);
      toast.error('Erro ao entrar no grupo');
    } finally {
      setJoiningGroup(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copiado!');
  };

  // Filtrar grupos
  const filteredGroups = (groupsWithLinks.length > 0 ? groupsWithLinks : groups).filter(g => {
    const matchesSearch = g.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (filterType === 'all') return true;
    if (filterType === 'with-link') {
      return g.invite_link || (g.found_links && g.found_links.length > 0);
    }
    if (filterType === 'no-link') {
      return !g.invite_link && (!g.found_links || g.found_links.length === 0);
    }
    
    return true;
  });

  const totalWithLinks = groupsWithLinks.filter(g => g.invite_link || g.found_links?.length > 0).length;
  const selectedUserData = users.find(u => u.id === selectedUser);

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent flex items-center gap-2">
            <Link2 className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
            Links de Grupos
          </h1>
          <p className="text-xs sm:text-base text-muted-foreground mt-0.5 sm:mt-1">
            Encontre links de grupos dos seus clientes/revendedores
          </p>
        </div>
        
        <Badge variant="outline" className="text-xs sm:text-sm border-green-500 text-green-600 self-start sm:self-auto">
          <Shield className="w-3 h-3 mr-1" />
          Apenas Admin
        </Badge>
      </div>

      {/* Selecionar Minha Conexão (para entrar nos grupos) */}
      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
            <User className="w-4 h-4 text-blue-500" />
            Sua Conexão (para entrar nos grupos)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedMyConnection} onValueChange={setSelectedMyConnection}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione sua conexão para entrar nos grupos" />
            </SelectTrigger>
            <SelectContent>
              {myConnections.map(conn => (
                <SelectItem key={conn.id} value={conn.id}>
                  {conn.name || conn.instance_name} (Sua)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {myConnections.length === 0 && (
            <p className="text-xs text-amber-600 mt-2">
              Você precisa de uma conexão ativa para poder entrar nos grupos
            </p>
          )}
        </CardContent>
      </Card>

      {/* Selecionar Cliente */}
      <Card>
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Building className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
            Selecionar Cliente/Revendedor
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione um cliente ou revendedor" />
            </SelectTrigger>
            <SelectContent>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>
                  <div className="flex items-center gap-2">
                    <span>{u.name || u.username}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {u.role === 'reseller' ? 'Revendedor' : 'Cliente'}
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {selectedUserData && (
            <div className="p-3 bg-muted/50 rounded-lg text-sm">
              <p><strong>Usuário:</strong> {selectedUserData.name || selectedUserData.username}</p>
              <p><strong>Email:</strong> {selectedUserData.email}</p>
              <p><strong>Tipo:</strong> {selectedUserData.role === 'reseller' ? 'Revendedor' : 'Cliente'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conexões do Cliente */}
      {selectedUser && userConnections.length > 0 && (
        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <UsersRound className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
              Conexões do Cliente ({userConnections.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={selectedConnection} onValueChange={setSelectedConnection}>
                <SelectTrigger className="w-full sm:flex-1">
                  <SelectValue placeholder="Selecione uma conexão do cliente" />
                </SelectTrigger>
                <SelectContent>
                  {userConnections.map(conn => {
                    // status pode ser um objeto {connected, jid, loggedIn} ou uma string
                    const statusStr = typeof conn.status === 'object' 
                      ? (conn.status?.connected ? 'connected' : 'disconnected')
                      : conn.status;
                    return (
                    <SelectItem key={conn.id} value={conn.id}>
                      <div className="flex items-center gap-2">
                        <span>{conn.name || conn.instance_name}</span>
                        <Badge variant={statusStr === 'connected' ? 'default' : 'secondary'} className="text-[10px]">
                          {statusStr === 'connected' ? 'Online' : statusStr || 'Desconhecido'}
                        </Badge>
                      </div>
                    </SelectItem>
                  )})}

                </SelectContent>
              </Select>
              
              <Button 
                onClick={fetchGroups} 
                disabled={!selectedConnection || loading}
                variant="outline"
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Carregar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedUser && userConnections.length === 0 && (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <UsersRound className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Este usuário não possui conexões</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Grupos */}
      {groups.length > 0 && (
        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                Grupos do Cliente ({groups.length})
                {totalWithLinks > 0 && (
                  <Badge className="bg-green-500 text-white text-xs ml-2">
                    {totalWithLinks} com links
                  </Badge>
                )}
              </CardTitle>
              
              <Button
                onClick={analyzeAllGroups}
                disabled={analyzing || groups.length === 0}
                className="bg-gradient-to-r from-green-600 to-emerald-600 w-full sm:w-auto"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Analisando... {analyzeProgress.toFixed(0)}%
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Buscar Links
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {analyzing && (
              <Progress value={analyzeProgress} className="h-2" />
            )}
            
            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar grupos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-sm"
                />
              </div>
              
              {groupsWithLinks.length > 0 && (
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="with-link">Com Link</SelectItem>
                    <SelectItem value="no-link">Sem Link</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Lista */}
            <div className="max-h-[500px] sm:max-h-[600px] overflow-y-auto space-y-3">
              {filteredGroups.map((group, idx) => (
                <div
                  key={group.jid || idx}
                  className="p-3 sm:p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex flex-col gap-3">
                    {/* Info do grupo */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-sm sm:text-base truncate">{group.name}</h3>
                        {group.analyzed && (
                          group.invite_link || group.found_links?.length > 0 ? (
                            <Badge className="bg-green-500 text-white text-xs">
                              <Globe className="w-3 h-3 mr-1" />
                              Link Disponível
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-muted-foreground">
                              <Lock className="w-3 h-3 mr-1" />
                              Privado
                            </Badge>
                          )
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {group.participants_count || '?'} membros
                      </p>
                      
                      {/* Descrição */}
                      {group.description && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2 bg-muted/50 p-2 rounded">
                          {group.description}
                        </p>
                      )}
                    </div>
                    
                    {/* Links e Ações */}
                    {group.analyzed && (group.invite_link || group.found_links?.length > 0) && (
                      <div className="space-y-2 pt-2 border-t">
                        {/* Link de convite oficial */}
                        {group.invite_link && (
                          <div className="flex flex-wrap items-center gap-2">
                            <code className="text-[10px] sm:text-xs bg-green-500/10 text-green-700 px-2 py-1 rounded truncate max-w-[200px] sm:max-w-[300px]">
                              {group.invite_link}
                            </code>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyToClipboard(group.invite_link)}
                                className="text-xs h-7 px-2"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(group.invite_link, '_blank')}
                                className="text-xs h-7 px-2"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => joinGroup(group.invite_link)}
                                disabled={joiningGroup === group.invite_link || !selectedMyConnection}
                                className="bg-green-600 hover:bg-green-700 text-xs h-7 px-2"
                              >
                                {joiningGroup === group.invite_link ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <UserPlus className="w-3 h-3 mr-1" />
                                    Entrar
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Links encontrados na descrição */}
                        {group.found_links && group.found_links.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs text-muted-foreground font-medium">Links na descrição:</p>
                            {group.found_links.map((link, linkIdx) => (
                              <div key={linkIdx} className="flex flex-wrap items-center gap-2">
                                <code className="text-[10px] sm:text-xs bg-blue-500/10 text-blue-700 px-2 py-1 rounded truncate max-w-[200px] sm:max-w-[300px]">
                                  {link}
                                </code>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => copyToClipboard(link)}
                                    className="h-6 w-6 p-0"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => window.open(link, '_blank')}
                                    className="h-6 w-6 p-0"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => joinGroup(link)}
                                    disabled={joiningGroup === link || !selectedMyConnection}
                                    className="bg-green-600 hover:bg-green-700 text-xs h-6 px-2"
                                  >
                                    {joiningGroup === link ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <UserPlus className="w-3 h-3" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {filteredGroups.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum grupo encontrado</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
